import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { CheckCircle2, ShieldCheck, Loader2, ArrowRight } from "lucide-react";

/**
 * Public lander rendered from a publisher-customised template.
 * URL: /l/:slug   Optional ?sub_id=… is captured for sub-source analytics.
 *
 * Brand-led, FCA-conscious design — clean white hero, theme-coloured CTA,
 * trust badges, sector-specific copy, no Connexis branding (publisher's white-label).
 */
export default function PublicLander() {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const subId = search.get("sub_id") || search.get("subid") || search.get("utm_source") || "";
  const aff   = search.get("aff") || "";

  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const viewFired = useRef(false);

  // Fetch config
  useEffect(() => {
    axios.get(`${API_BASE}/public/landers/${encodeURIComponent(slug)}`)
      .then((r) => setConfig(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Page not found"));
  }, [slug]);

  // Fire view exactly once per page-load
  useEffect(() => {
    if (!config || viewFired.current) return;
    viewFired.current = true;
    axios.post(`${API_BASE}/public/landers/${encodeURIComponent(slug)}/view`, {
      sub_id: subId,
      variant_id: config.variant_id || undefined,
    }).catch(() => {});
    if (typeof document !== "undefined") document.title = config.headline || "Quote — Connexis Partner";
  }, [config, slug, subId]);

  // CSS variables from theme
  const themeVars = useMemo(() => {
    if (!config?.theme) return {};
    return {
      "--lander-primary": config.theme.primary || "#0055FF",
      "--lander-accent":  config.theme.accent  || "#0F172A",
    };
  }, [config]);

  const onChange = (name, v) => setValues((prev) => ({ ...prev, [name]: v }));

  const fixedNames = new Set(["first_name", "last_name", "email", "phone", "postcode"]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Split fields between top-level required ones and extras
      const top = {};
      const extras = {};
      Object.entries(values).forEach(([k, v]) => {
        if (fixedNames.has(k)) top[k] = (v || "").toString().trim();
        else if ((v || "").toString().trim()) extras[k] = (v || "").toString().trim();
      });
      const payload = {
        first_name: top.first_name || "",
        last_name:  top.last_name  || "",
        email:      top.email      || "",
        phone:      top.phone      || "",
        postcode:   top.postcode   || undefined,
        sub_id:     subId || undefined,
        aff:        aff   || undefined,
        variant_id: config?.variant_id || undefined,
        extra_fields: extras,
      };
      const res = await axios.post(`${API_BASE}/public/landers/${encodeURIComponent(slug)}/submit`, payload);
      setSubmitted(res.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setSubmitted({ ok: false, message: typeof d === "string" ? d : "Submission failed — please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 px-6">
        <div className="text-center max-w-md">
          <div className="text-5xl mb-4">404</div>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }
  if (!config) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="size-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-white text-slate-900"
      style={themeVars}
      data-testid="public-lander"
    >
      {/* Hero strip */}
      <div className="border-b border-slate-200 bg-gradient-to-br from-slate-50 to-white">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-12 sm:py-16 grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14">
          <div className="self-center">
            {config.hero_eyebrow && (
              <div
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em]"
                style={{ background: "color-mix(in srgb, var(--lander-primary) 10%, transparent)", color: "var(--lander-primary)" }}
                data-testid="lander-eyebrow"
              >
                <ShieldCheck className="size-3.5" /> {config.hero_eyebrow}
              </div>
            )}
            <h1 className="mt-4 text-4xl sm:text-5xl font-semibold leading-[1.05] tracking-tight" data-testid="lander-headline">
              {config.headline}
            </h1>
            {config.subheadline && (
              <p className="mt-4 text-[15.5px] sm:text-base text-slate-600 max-w-xl leading-relaxed" data-testid="lander-subheadline">
                {config.subheadline}
              </p>
            )}
            {Array.isArray(config.bullets) && config.bullets.length > 0 && (
              <ul className="mt-6 space-y-2.5">
                {config.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[14.5px] text-slate-700">
                    <CheckCircle2 className="size-[18px] mt-0.5 shrink-0" style={{ color: "var(--lander-primary)" }} />
                    {b}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Form card */}
          <div className="lg:pl-4">
            <div
              className="rounded-2xl border border-slate-200 shadow-xl shadow-slate-900/[0.04] bg-white p-6 sm:p-8"
              data-testid="lander-form-card"
            >
              {submitted?.ok ? (
                <div className="py-8 text-center" data-testid="lander-success">
                  <div
                    className="size-12 rounded-full mx-auto mb-4 grid place-items-center"
                    style={{ background: "color-mix(in srgb, var(--lander-primary) 14%, transparent)", color: "var(--lander-primary)" }}
                  >
                    <CheckCircle2 className="size-6" />
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900">You're all set.</h2>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{submitted.message}</p>
                </div>
              ) : submitted && submitted.ok === false ? (
                <div className="py-8 text-center" data-testid="lander-soft-fail">
                  <div className="size-12 rounded-full mx-auto mb-4 grid place-items-center bg-slate-100 text-slate-700">
                    <CheckCircle2 className="size-6" />
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900">Thanks — we've got your details.</h2>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{submitted.message}</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    {config.fields.map((f) => (
                      <FieldRow key={f.name} field={f} value={values[f.name]} onChange={onChange} />
                    ))}
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 rounded-lg text-white font-semibold text-[15px] inline-flex items-center justify-center gap-1.5 disabled:opacity-70 transition-transform hover:-translate-y-px active:translate-y-0"
                    style={{ background: "var(--lander-primary)" }}
                    data-testid="lander-submit"
                  >
                    {submitting ? <Loader2 className="size-4 animate-spin" /> : (
                      <>{config.cta_label} <ArrowRight className="size-4" /></>
                    )}
                  </button>
                  <p className="text-[11.5px] text-slate-500 leading-relaxed">
                    By submitting your details you consent to be contacted by {config.publisher_company} or its partners about your enquiry.
                    See our <a href="/legal/privacy" className="underline">privacy policy</a> for full details.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Trust strip */}
      {Array.isArray(config.trust_badges) && config.trust_badges.length > 0 && (
        <div className="border-b border-slate-100 bg-slate-50/60" data-testid="lander-trust-strip">
          <div className="mx-auto max-w-6xl px-5 sm:px-8 py-5 flex flex-wrap items-center justify-center gap-x-7 gap-y-2">
            {config.trust_badges.map((b, i) => (
              <div key={i} className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-600">
                <ShieldCheck className="size-3.5" style={{ color: "var(--lander-primary)" }} />
                {b}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Disclosure footer */}
      {config.disclosure && (
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-10 text-center" data-testid="lander-disclosure">
          <p className="text-[12px] text-slate-500 leading-relaxed">{config.disclosure}</p>
        </div>
      )}
    </div>
  );
}

function FieldRow({ field, value, onChange }) {
  const { name, label, type, required, options, placeholder } = field;
  const isFullWidth = ["email", "select", "textarea"].includes(type);
  const wrapCls = isFullWidth ? "col-span-2" : "col-span-2 sm:col-span-1";
  return (
    <div className={wrapCls}>
      <label htmlFor={`f-${name}`} className="block text-[12.5px] font-medium text-slate-700 mb-1.5">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </label>
      {type === "select" ? (
        <select
          id={`f-${name}`}
          required={!!required}
          value={value || ""}
          onChange={(e) => onChange(name, e.target.value)}
          className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-offset-1"
          style={{ "--tw-ring-color": "var(--lander-primary)" }}
          data-testid={`lander-field-${name}`}
        >
          <option value="">Choose one…</option>
          {(options || []).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
      ) : (
        <input
          id={`f-${name}`}
          type={type === "tel" ? "tel" : type === "email" ? "email" : "text"}
          required={!!required}
          autoComplete={
            type === "email" ? "email"
            : type === "tel" ? "tel"
            : name === "first_name" ? "given-name"
            : name === "last_name" ? "family-name"
            : name === "postcode" ? "postal-code"
            : "off"
          }
          placeholder={placeholder || ""}
          value={value || ""}
          onChange={(e) => onChange(name, e.target.value)}
          className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-offset-1"
          style={{ "--tw-ring-color": "var(--lander-primary)" }}
          data-testid={`lander-field-${name}`}
        />
      )}
    </div>
  );
}
